<script>
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import Button from '$lib/atoms/Button.svelte';
	import SearchBar from '$lib/components/SearchBar.svelte';
	import { setLanguage, supportedLanguages, t } from '$lib/i18n.js';
	import { authStore } from '$lib/stores/auth.js';
	import { pushStore } from '$lib/stores/push.js';
	import { webPushEnabled } from '$lib/stores/feature-flags.js';
	import ArrowRight from 'lucide-svelte/icons/arrow-right';
	import Menu from 'lucide-svelte/icons/menu';
	import { onMount } from 'svelte';

	export let foregroundColor = 'var(--on-surface)';
	export let backgroundColor = 'var(--surface-container)';
	export let logoAndTextsColor = 'var(--on-surface)';
	export let logoInverted = false;
	export let showSearchBar = false;
	export let searchBarRedirectUrl = '';

	// Search data props for SearchBar
	export let dataInizio = '';
	export let dataFine = '';
	export let lat = '';
	export let long = '';
	export let pid = '';
	export let address = 'Current location';
	let navBarBackgroundColor = 'transparent';
	let isScrolled = false;
	
	let mainMenuOpen = false;
	let languageDialogOpen = false;

	// Colori dinamici basati sullo scroll
	$: dynamicNavBarBackgroundColor = isScrolled ? 'var(--surface)' : 'transparent';
	$: dynamicForegroundColor = isScrolled ? 'var(--on-surface)' : foregroundColor;
	$: dynamicBackgroundColor = isScrolled ? 'var(--surface-container)' : backgroundColor;
	$: dynamicLogoAndTextsColor = isScrolled ? 'var(--on-surface)' : logoAndTextsColor;
	$: dynamicLogoInvertFilter = isScrolled ? '1' : logoInverted ? '0' : '1';

	onMount(() => {
		const handleScroll = () => {
			isScrolled = window.scrollY > 50;
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	});
	
	// Languages data
	const languages = {
		'en': { name: 'English', flag: '🇬🇧' },
		'it': { name: 'Italiano', flag: '🇮🇹' }, 
		'fr': { name: 'Français', flag: '🇫🇷' }
	};
	
	// Toggle main menu
	function toggleMainMenu() {
		mainMenuOpen = !mainMenuOpen;
		// Chiudi dialog lingue se aperto
		languageDialogOpen = false;
		// Inizializza lo stato delle notifiche push quando il menu si apre (solo loggati)
		if (mainMenuOpen && $authStore.isAuthenticated) {
			pushStore.init();
		}
	}

	// Attiva/disattiva le notifiche push
	function togglePush() {
		if ($pushStore.loading || $pushStore.permission === 'denied') return;
		if ($pushStore.enabled) {
			pushStore.disable();
		} else {
			pushStore.enable();
		}
	}
	
	// Chiudi main menu
	function closeMainMenu() {
		mainMenuOpen = false;
	}

	// Logout
	function logout() {
		authStore.logout();
		closeMainMenu();
	}
	
	// Apri dialog lingue
	function openLanguageSelector() {
		languageDialogOpen = true;
		// Chiudi main menu se aperto
		mainMenuOpen = false;
	}
	
	// Chiudi dialog lingue
	function closeLanguageDialog() {
		languageDialogOpen = false;
	}
	
	// Seleziona lingua
	function selectLanguage(selectedLang) {
		const currentLang = $page.params.lang;
		
		if (selectedLang !== currentLang) {
			localStorage.setItem('language', selectedLang);
			setLanguage(selectedLang);
			const newPath = $page.url.pathname.replace(`/${currentLang}`, `/${selectedLang}`);
			const newUrl = newPath + $page.url.search + $page.url.hash;
			goto(newUrl);
		}
		
		closeLanguageDialog();
	}
	
	// Chiudi dialog quando si clicca fuori
	function handleDialogBackdropClick(event) {
		if (event.target === event.currentTarget) {
			closeLanguageDialog();
		}
	}
	
	// Chiudi menu quando si clicca fuori
	function handleMenuBackdropClick(event) {
		if (event.target === event.currentTarget) {
			closeMainMenu();
		}
	}
	
	// Helper function to extract time from YYYYMMDDHHMM format
	function formatTimeFromCustomDate(dateStr) {
		if (!dateStr || dateStr.length !== 12) return '';
		try {
			const hour = dateStr.substring(8, 10);
			const minute = dateStr.substring(10, 12);
			return `${hour}:${minute}`;
		} catch (error) {
			return '';
		}
	}
	
	// Helper function to convert YYYYMMDDHHMM to YYYY-MM-DD format
	function formatDateFromCustomDate(dateStr) {
		if (!dateStr || dateStr.length !== 12) return '';
		try {
			const year = dateStr.substring(0, 4);
			const month = dateStr.substring(4, 6);
			const day = dateStr.substring(6, 8);
			return `${year}-${month}-${day}`;
		} catch (error) {
			return '';
		}
	}
</script>

<nav class="navbar {showSearchBar ? 'search-active' : ''}" style="--navBarBackground: {dynamicNavBarBackgroundColor}; --navBarShadow: {isScrolled ? '0 2px 10px rgba(0, 0, 0, 0.1)' : 'none'};">
	<div class="nav-container">
		<!-- Logo a sinistra -->
		<div class="nav-logo">
			<a href="/{$page.params.lang}">
				<img src="https://assets.auting.it/images/logo/logo-light.svg" alt="Logo" style="filter: brightness({dynamicLogoInvertFilter}) invert({1-dynamicLogoInvertFilter});" />
			</a>
		</div>
		
		<!-- SearchBar al centro (quando showSearchBar è true) -->
		{#if showSearchBar}
		<div class="nav-center" style="{showSearchBar ? 'max-width: 1200px; padding-left: 32px; padding-right: 32px;' : ''}">
			<SearchBar
				unstyled={true}
				redirectUrl={searchBarRedirectUrl || `/${$page.params.lang}/search`}
				initialAddress={address}
				initialLat={parseFloat(lat) || null}
				initialLng={parseFloat(long) || null}
				initialPid={pid}
				initialStartDate={formatDateFromCustomDate(dataInizio)}
				initialStartTime={formatTimeFromCustomDate(dataInizio)}
				initialEndDate={formatDateFromCustomDate(dataFine)}
				initialEndTime={formatTimeFromCustomDate(dataFine)}
			/>
		</div>
		{/if}
		
		<!-- Desktop: How it works link + Publish button + Language + Menu button -->
		<div class="nav-right desktop-only">
			
			<div class="nav-buttons">
				{#if !showSearchBar}
				<Button 
					label={$t('components.nav.publish_your_car')} 
					backgroundColor={dynamicBackgroundColor}
					textColor={dynamicForegroundColor}
					onclick={() => goto(`/${$page.params.lang}/owner`)}
				/>
			{/if}

			<button 
				class="language-btn" 
				style="background-color: {dynamicBackgroundColor}; color: {dynamicForegroundColor};"
				onclick={openLanguageSelector}
				type="button"
				aria-label={$t('components.nav.change_language')}
			>
				{languages[$page.params.lang]?.flag || '🌐'}
			</button>

			{#if $authStore.isAuthenticated}
				
				<Button 
					label={$authStore.user?.name || 'User'} 
					backgroundColor={dynamicBackgroundColor}
					textColor={dynamicForegroundColor}
					onclick={() => { goto(`/account/publicProfile`); closeMainMenu(); }}
				/>
			{/if}

			<Button 
				icon={Menu}
				ariaLabel={$t('components.nav.open_menu')}
				backgroundColor={dynamicBackgroundColor}
				textColor={dynamicForegroundColor}
				onclick={toggleMainMenu}
				
			/>
			</div>
		</div>
		
		<!-- Mobile: Language + Menu button only -->
		<div class="nav-right mobile-only">
			<button 
				class="language-btn"
				style="background-color: {dynamicBackgroundColor}; color: {dynamicForegroundColor};"	 
				onclick={openLanguageSelector}
				type="button"
				aria-label={$t('components.nav.change_language')}
			>
				{languages[$page.params.lang]?.flag || '🌐'}
			</button>
			
			<Button 
				icon={Menu}
				ariaLabel={$t('components.nav.open_menu')}
				backgroundColor={dynamicBackgroundColor}
				textColor={dynamicForegroundColor}
				onclick={toggleMainMenu}
				
			/>
		</div>
	</div>
</nav>

<!-- Main Menu -->
{#if mainMenuOpen}
	<div 
		class="menu-backdrop" 
		onclick={handleMenuBackdropClick}
		onkeydown={(e) => e.key === 'Escape' && closeMainMenu()}
		role="dialog"
		aria-modal="true"
		aria-labelledby="main-menu-title"
		tabindex="-1"
	>
		<div class="menu-container">
			<div class="menu-header">
				<button style="opacity: 0" class="close-btn" onclick={closeMainMenu} aria-label="Close">
					✕
				</button>
				<img src="https://assets.auting.it/images/logo/logo.svg" alt="Logo" class="menu-logo" />
				<button class="close-btn" onclick={closeMainMenu} aria-label="Close">
					✕
				</button>
			</div>
			<!-- Menu Links -->
			<div class="menu-links">
				<a href="/{$page.params.lang}/driver" onclick={closeMainMenu} aria-label={$t('components.nav.how_it_works')}>
					{$t('components.nav.how_it_works')}
				</a>
				{#if $authStore.isAuthenticated}
					<a href="/carList" data-sveltekit-reload onclick={closeMainMenu} aria-label="Your cars">
						{$t('components.nav.cars')}
					</a>
					<a href="/viaggi/owner" data-sveltekit-reload onclick={closeMainMenu} aria-label="Trips with your cars">
						{$t('components.nav.trips_owner')}
					</a>
					<a href="/viaggi/driver" data-sveltekit-reload onclick={closeMainMenu} aria-label="Your trips">
						{$t('components.nav.trips_driver')}
					</a>
					<a href="/account/revenue" data-sveltekit-reload onclick={closeMainMenu} aria-label="Revenues">
						{$t('components.nav.earnings')}
					</a>
					<a href="https://auting.it/account/personalData" onclick={closeMainMenu} aria-label="Personal Data">
						{$t('components.nav.documents')}
					</a>
					{#if $webPushEnabled && $pushStore.permission !== 'unsupported'}
						<div class="push-row">
							<button
								type="button"
								class="push-toggle"
								class:on={$pushStore.enabled}
								disabled={$pushStore.loading || $pushStore.permission === 'denied'}
								aria-pressed={$pushStore.enabled}
								onclick={togglePush}
							>
								<span>{$t('components.nav.push_notifications')}</span>
								<span class="switch" aria-hidden="true"><span class="knob"></span></span>
							</button>
							{#if $pushStore.permission === 'denied'}
								<small class="push-hint">{$t('components.nav.push_denied_hint')}</small>
							{:else if $pushStore.error}
								<small class="push-hint">{$t('components.nav.push_error')}</small>
							{/if}
						</div>
					{/if}
					<a href="/" onclick={logout} aria-label="Logout">
						{$t('components.nav.logout')}
					</a>
				{:else}
					<a href="/{$page.params.lang}/login" onclick={closeMainMenu} aria-label={$t('components.nav.sign_in')}>
						{$t('components.nav.sign_in')}
					</a>
				{/if}
				<a href="https://auting.notion.site" onclick={closeMainMenu} aria-label={$t('components.nav.faq')}>
					{$t('components.nav.faq')}
				</a>
			</div>
			
			<!-- Menu Actions -->
			<div class="menu-actions">
				{#if $authStore.isAuthenticated}
				<Button 
					icon={ArrowRight}
					label={$t('components.nav.profile')}
					backgroundColor="var(--orange)"
					textColor="var(--on-primary)"
					size="full-width"
					hasShadow={true}
					onclick={() => { goto(`/account/publicProfile`); closeMainMenu(); }}
				/>
				{:else}
				<Button 
					icon={ArrowRight}
					label={$t('components.nav.get_started')}
					backgroundColor="var(--orange)"
					textColor="var(--on-primary)"
					size="full-width"
					hasShadow={true}
					onclick={() => { goto(`/${$page.params.lang}/register`); closeMainMenu(); }}
				/>
				{/if}
				
				<Button 
					icon={ArrowRight}
					label={$t('components.nav.publish_your_car')}
					backgroundColor="var(--water-green)"
					textColor="var(--on-primary)"
					size="full-width"
					hasShadow={true}
					onclick={() => { goto(`/${$page.params.lang}/owner`); closeMainMenu(); }}
				/>
			</div>
		</div>
	</div>
{/if}

<!-- Language Dialog -->
{#if languageDialogOpen}
	<div 
		class="language-dialog-backdrop" 
		onclick={handleDialogBackdropClick}
		onkeydown={(e) => e.key === 'Escape' && closeLanguageDialog()}
		role="dialog"
		aria-modal="true"
		aria-labelledby="language-dialog-title"
		tabindex="-1"
	>
		<div class="language-dialog">
			<div class="dialog-header">
				<button class="close-btn" onclick={closeLanguageDialog} aria-label="Close">
					✕
				</button>
			</div>
			
			<div class="language-options">
				{#each supportedLanguages as lang}
					<button 
						class="language-option"
						class:active={$page.params.lang === lang}
						onclick={() => selectLanguage(lang)}
					>
						<span class="flag">{languages[lang].flag}</span>
						<span class="language-name">{languages[lang].name}</span>
						{#if $page.params.lang === lang}
							<span class="checkmark">✓</span>
						{/if}
					</button>
				{/each}
			</div>
		</div>
	</div>
{/if}

<style>
	/* ==============================================
	   🎯 NAVBAR
	   ============================================== */
	
	.navbar {
		background: var(--navBarBackground, transparent);
		box-shadow: var(--navBarShadow, none);
		position: fixed;
		top: 0;
		left: 0px;
		width: 100%;
		z-index: 1000;
		transition: all 0.3s ease;
	}

	.nav-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		max-width: var(--max-width);
		margin: 0 auto;
		padding: 1rem 2rem;
	}

	.nav-logo a {
		font-size: 1.5rem;
		font-weight: bold;
		color: var(--color-theme-1);
		text-decoration: none;
	}

	.nav-logo a img {
		max-width: 120px;
	}

	.nav-center {
		flex: 9;
		width: 100%;
		display: flex;
		justify-content: center;
		align-items: center;
		max-width: 600px;
		height: 32px;
	}

	.nav-right {
		display: flex;
		align-items: center;
		gap: 24px;
	}

	.nav-buttons {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.nav-link {
		color: var(--color-text);
		text-decoration: none;
		font-weight: 500;
		transition: color 0.2s ease;
	}

	.nav-link:hover {
		color: var(--color-theme-1);
	}

	/* Language Button */
	.language-btn {
		border: 2px solid transparent;
		border-radius: 40px;
		width: 40px;
		height: 40px;
		color: var(--color-theme-1);
		font-size: 0.9rem;
		font-weight: 500;
		cursor: pointer;
		transition: all 0.2s ease;
	}

	.language-btn:hover {
		background: var(--color-theme-1);
		color: white;
		transform: translateY(-1px);
	}

	/* ==============================================
	   🎯 MAIN MENU
	   ============================================== */

	.menu-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.25);
		backdrop-filter: blur(24px);
		z-index: 2000;
		animation: fadeIn 0.3s ease;
	}

	@keyframes fadeIn {
		from { opacity: 0; }
		to { opacity: 1; }
	}

	/* Desktop: Sidebar laterale a destra */
	.menu-container {
		position: fixed;
		top: 24px;
		right: 2rem;
		background: white;
		border-radius: 24px;
		box-shadow: 16px 16px 32px rgba(0, 0, 0, 0.16);
		padding: 2rem;
		min-width: 400px;
		animation: slideInRight 0.3s ease;
		gap: 40px;
		height: calc(100% - 48px);
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		align-items: stretch;
	}

	.menu-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		flex-direction: row;
	}

	@keyframes slideInRight {
		from { 
			opacity: 0;
			transform: translateY(0%) translateX(100%);
		}
		to { 
			opacity: 1;
			transform: translateY(0%) translateX(0);
		}
	}

	.menu-links {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 0.7rem;
	}

	.menu-links a {
		color: var(--color-text);
		text-decoration: none;
		font-weight: 500;
		font-size: 1.1rem;
		transition: color 0.2s ease;
	}

	.menu-links a:hover {
		color: var(--color-theme-1);
	}

	.push-row {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
	}

	.push-toggle {
		display: flex;
		align-items: center;
		gap: 12px;
		border: none;
		background: none;
		cursor: pointer;
		font-weight: 500;
		font-size: 1.1rem;
		color: var(--color-text);
		padding: 0;
		font-family: inherit;
	}

	.push-toggle:disabled {
		opacity: 0.5;
		cursor: default;
	}

	.push-toggle .switch {
		position: relative;
		width: 40px;
		height: 22px;
		border-radius: 22px;
		background: #ccc;
		transition: background 0.2s ease;
		flex-shrink: 0;
	}

	.push-toggle.on .switch {
		background: var(--orange, #f60);
	}

	.push-toggle .knob {
		position: absolute;
		top: 2px;
		left: 2px;
		width: 18px;
		height: 18px;
		border-radius: 50%;
		background: white;
		transition: transform 0.2s ease;
	}

	.push-toggle.on .knob {
		transform: translateX(18px);
	}

	.push-hint {
		color: #888;
		font-size: 0.75rem;
		max-width: 240px;
		text-align: center;
	}

	.menu-actions {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	/* ==============================================
	   🎯 LANGUAGE DIALOG
	   ============================================== */

	.language-dialog-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.5);
		backdrop-filter: blur(4px);
		display: flex;
		justify-content: center;
		align-items: center;
		z-index: 3000;
		animation: fadeIn 0.2s ease;
	}

	.language-dialog {
		background: white;
		border-radius: 12px;
		box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
		min-width: 320px;
		max-width: 400px;
		animation: scaleIn 0.3s ease;
		overflow: hidden;
	}

	@keyframes scaleIn {
		from { 
			opacity: 0;
			transform: scale(0.9) translateY(-10px);
		}
		to { 
			opacity: 1;
			transform: scale(1) translateY(0);
		}
	}

	.dialog-header {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		padding: 1rem;
	}

	.dialog-header h3 {
		margin: 0;
		font-size: 1.2rem;
		font-weight: 600;
		color: var(--color-theme-2);
	}

	.close-btn {
		background: none;
		border: none;
		font-size: 1.5rem;
		cursor: pointer;
		color: #666;
		padding: 0.5rem;
		border-radius: 50%;
		transition: all 0.2s ease;
		line-height: 1;
		aspect-ratio: 1;
		width: 48px;
		height: 48px;
	}

	.close-btn:hover {
		background: rgba(0, 0, 0, 0.1);
		color: #333;
	}

	.language-options {
		padding: 1rem 0;
	}

	.language-option {
		display: flex;
		align-items: center;
		width: 100%;
		padding: 1rem 1.5rem;
		border: none;
		background: none;
		cursor: pointer;
		transition: all 0.2s ease;
		text-align: left;
		gap: 1rem;
	}

	.language-option:hover {
		background: #f8f9ff;
	}

	.language-option.active {
		background: linear-gradient(135deg, #fff3e0 0%, #ffe8cc 100%);
		border-left: 4px solid var(--color-theme-1);
	}

	.language-option .flag {
		font-size: 1.5rem;
		min-width: 2rem;
	}

	.language-option .language-name {
		flex: 1;
		font-size: 1rem;
		font-weight: 500;
		color: var(--color-text);
	}

	.language-option.active .language-name {
		color: var(--color-theme-1);
		font-weight: 600;
	}

	.language-option .checkmark {
		color: var(--color-theme-1);
		font-weight: bold;
		font-size: 1.2rem;
	}

	.dialog-footer {
		padding: 1rem 1.5rem;
		border-top: 1px solid #f0f0f0;
		background: #fafafa;
		text-align: center;
	}

	.cancel-btn {
		background: #f5f5f5;
		border: none;
		padding: 0.5rem 1.5rem;
		border-radius: 6px;
		cursor: pointer;
		color: #666;
		font-weight: 500;
		transition: all 0.2s ease;
	}

	.cancel-btn:hover {
		background: #e0e0e0;
		color: #333;
	}

	.mobile-only {
		display: none;
	}

	/* ==============================================
	   🎯 MOBILE
	   ============================================== */

	@media (max-width: 767px) {
		.search-active {
			display: none;
		}
		
		.nav-container {
			padding: 1rem;
		}
		
		.nav-logo a {
			font-size: 1.3rem;
		}

		.nav-logo a img {
			max-width: 120px;
		}
		
		.language-btn {
			padding: 0.4rem 0.8rem;
			font-size: 0.8rem;
		}

		/* Mobile: Menu full-screen */
		.menu-container {
			position: fixed;
			top: 16px;
			left: 8px;
			right: 8px;
			transform: none;
			border-radius: 24px;
			animation: slideInMobile 0.3s ease;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			align-items: stretch;
			padding-top: 24px;
			padding-bottom: 24px;
			padding-left: 16px;
			padding-right: 16px;
			min-width: 300px;
			height: auto;
		}

		@keyframes slideInMobile {
			from { 
				opacity: 0;
				transform: translateY(20px);
			}
			to { 
				opacity: 1;
				transform: translateY(0);
			}
		}

		.menu-links {
			gap: 16px;
		}

		.menu-links a {
			font-size: 1.5rem;
			margin: 0;
			padding: 0;
			letter-spacing: -1px;
			font-weight: 500;
			font-size: 24px;
		}

		.language-dialog {
			margin: 1rem;
			min-width: auto;
			max-width: none;
			width: 100%;
		}

		.dialog-header {
			padding: 1rem;
		}

		.dialog-header h3 {
			font-size: 1.1rem;
		}

		.language-option {
			padding: 0.75rem 1rem;
		}

		.nav-right.mobile-only {
			display: flex;
		flex-direction: row;
		align-items: center;
		gap: 8px;
		}

		.nav-right.desktop-only {
			display: none;
		}

		.language-btn {
			width: 56px;
			height: 56px;
			font-size: 20px;
		}
	}
</style>